// components/organisms/ModalEditNote/ModalEditNote.tsx
import * as React from 'react';
import { Box, Button, CircularProgress, Typography, useTheme, useMediaQuery } from '@mui/material';
import { Switch } from '../../atoms';
import { NoteEditor } from '../../molecules/NoteEditor/NoteEditor';

export type ModalEditNoteProps = {
    initialContent?: string;
    initialHighlighted?: boolean;
    initialMemoClient?: boolean;
    isSaving?: boolean;
    onConfirm?: (content: string, highlighted: boolean, memoClient: boolean) => void;
    onCancel?: () => void;
};

export const ModalEditNote: React.FunctionComponent<ModalEditNoteProps> = function ({
    initialContent = '',
    initialHighlighted = false,
    initialMemoClient = false,
    isSaving = false,
    onConfirm,
    onCancel,
}) {
    const theme = useTheme();
    const { palette, breakpoints } = theme;

    const downLgBreakpoint = useMediaQuery(breakpoints.down('lg'));
    const formWidth = downLgBreakpoint ? 'calc(100vw - 200px)' : '1098px';

    const [content, setContent] = React.useState(initialContent);
    const [highlighted, setHighlighted] = React.useState(initialHighlighted);
    const [memoClient, setMemoClient] = React.useState(initialMemoClient);
    const [error, setError] = React.useState('');

    // Synchroniser avec les valeurs initiales quand elles changent
    React.useEffect(() => {
        setContent(initialContent);
        setHighlighted(initialHighlighted);
        setMemoClient(initialMemoClient);
    }, [initialContent, initialHighlighted, initialMemoClient]);

    const handleConfirm = () => {
        if (!content.trim() || !onConfirm) {
            setError('Le contenu de la note est obligatoire');
            return;
        }

        if (content.trim() && onConfirm) {
            onConfirm(content.trim(), highlighted, memoClient);
        }
    };

    const handleCancel = () => {
        if (!isSaving && onCancel) {
            onCancel();
        }
    };

    const submitButtons: React.ReactNode = (
        <Box
            sx={{
                backgroundColor: palette.background.dark,
                position: 'sticky',
                left: '0',
                right: '0',
                bottom: '0',
                display: 'flex',
                flexDirection: downLgBreakpoint ? 'column' : 'row',
                justifyContent: 'center',
                gap: '16px',
                height: downLgBreakpoint ? 'auto' : '100px',
                alignItems: 'center',
                padding: downLgBreakpoint ? '20px' : '',
            }}
        >
            <Button
                onClick={handleCancel}
                disabled={isSaving}
                style={{ marginTop: '8px', marginBottom: '8px' }}
                sx={{
                    color: palette.text.primary,
                    padding: '16px 24px 16px 24px',
                    width: downLgBreakpoint ? '100%' : 'auto',
                    display: downLgBreakpoint ? 'block' : 'flex',
                }}
            >
                Annuler
            </Button>
            <Button
                color="primary"
                variant="contained"
                onClick={handleConfirm}
                disabled={!content.trim() || isSaving}
                style={{ marginTop: '8px', marginBottom: '8px' }}
                startIcon={isSaving ? <CircularProgress size={16} /> : undefined}
                sx={{
                    backgroundColor: palette.primary.main,
                    color: palette.primary.contrastText,
                    padding: '16px 24px 16px 24px',
                    width: downLgBreakpoint ? '100%' : 'auto',
                    display: downLgBreakpoint ? 'block' : 'flex',
                }}
            >
                {isSaving ? 'Modification...' : 'Modifier la note'}
            </Button>
        </Box>
    );

    return (
        <Box
            sx={{
                width: formWidth,
                margin: '0 auto',
            }}
        >
            <Box
                sx={{
                    width: downLgBreakpoint ? '100%' : '80%',
                    maxWidth: downLgBreakpoint ? '100%' : '800px',
                    margin: '0 auto',
                }}
            >
                <Box
                    sx={{
                        marginBottom: '10px',
                    }}
                >
                    <Typography variant="l2dh6">Modifier cette note</Typography>
                </Box>
                <Box sx={{ padding: '0 24px' }}>
                    <NoteEditor
                        value={content}
                        onChange={setContent}
                        disabled={isSaving}
                        error={error}
                        placeholder="Modifiez le contenu de votre note...

**Formatage disponible :**
- **Gras** avec **texte**
- *Italique* avec *texte*
- `Code` avec `texte`
- # Titre principal
- ## Sous-titre
- - Liste à puces
- 1. Liste numérotée
- > Citation
- [Lien](https://example.com)"
                    />
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            mt: 2,
                        }}
                    >
                        <Typography variant="l2dbodys">Note importante</Typography>
                        <Switch
                            checked={highlighted}
                            onChange={(e) => setHighlighted(e.target.checked)}
                            disabled={isSaving}
                        />
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            mt: 2,
                        }}
                    >
                        <Typography variant="l2dbodys">Note mémo client</Typography>
                        <Switch
                            checked={memoClient}
                            onChange={(e) => setMemoClient(e.target.checked)}
                            disabled={isSaving}
                        />
                    </Box>
                </Box>
                {submitButtons}
            </Box>
        </Box>
    );
};
